import { z } from 'zod';
export const UserRoleSchema = z.enum(['ADMIN', 'EDITOR', 'VIEWER']);
// ─── Organization ────────────────────────────────────────────────────────────
export const OrganizationSchema = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const UpdateOrganizationInputSchema = z.object({
    name: z.string().min(1).max(200).optional(),
});
// ─── User ────────────────────────────────────────────────────────────────────
export const UserSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    email: z.string().email(),
    name: z.string().nullable(),
    role: UserRoleSchema,
    createdAt: z.date(),
    updatedAt: z.date(),
    lastLoginAt: z.date().nullable(),
});
export const CreateUserInputSchema = z.object({
    email: z.string().email(),
    name: z.string().min(1).max(200).optional(),
    role: UserRoleSchema.default('EDITOR'),
});
export const UpdateUserInputSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    role: UserRoleSchema.optional(),
});
// ─── ServiceAccount ──────────────────────────────────────────────────────────
export const ServiceAccountSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    scopes: z.array(z.string()),
    createdAt: z.date(),
    lastUsedAt: z.date().nullable(),
    revokedAt: z.date().nullable(),
});
/** Returned ONCE at creation — includes the raw key; never returned again. */
export const CreateServiceAccountResultSchema = ServiceAccountSchema.extend({
    rawKey: z.string(),
});
export const CreateServiceAccountInputSchema = z.object({
    name: z.string().min(1).max(200),
    scopes: z.array(z.string()).default([]),
});
// ─── Personal Access Token ─────────────────────────────────────────────────────
// A long-lived, org-scoped credential a user mints to call the Keycloak-guarded
// API headlessly. The PAT is not a JWT; it is presented to the exchange endpoint,
// which swaps a valid PAT for a short-lived Keycloak user access token.
/** PAT metadata. Never carries the raw secret (see CreatePersonalAccessTokenResult). */
export const PersonalAccessTokenSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    /** Keycloak user id (`sub`) the token acts as on exchange. Also the creator. */
    keycloakUserId: z.string(),
    /**
     * Which login the minting user held: `keycloak` (a real Keycloak `sub` that
     * can be impersonated on exchange) vs `robotics` (a Formant/Coherence login —
     * `keycloakUserId` holds a Formant userId that is NOT a Keycloak subject).
     * Drives how the PAT redeems. Absent on legacy rows, treated as `keycloak`.
     */
    tokenSource: z.enum(['keycloak', 'robotics']).optional(),
    /** Creator's email, snapshotted at mint time. Null if unknown. */
    createdByEmail: z.string().nullable(),
    /** Creator's display name, snapshotted at mint time. Null if unknown. */
    createdByName: z.string().nullable(),
    name: z.string(),
    /** Leading chars of the raw secret, kept for UI disambiguation only. */
    tokenPrefix: z.string(),
    scopes: z.array(z.string()),
    expiresAt: z.date().nullable(),
    lastUsedAt: z.date().nullable(),
    revokedAt: z.date().nullable(),
    createdAt: z.date(),
});
export const CreatePersonalAccessTokenInputSchema = z.object({
    name: z.string().min(1).max(200),
    /** OAuth scopes to request on exchange. `organization` is required for the
     *  exchanged token to carry the org claim; it is force-included server-side. */
    scopes: z.array(z.string()).default(['openid', 'organization']),
    /** Days until the token expires. Omit for a non-expiring token (until revoked). */
    expiresInDays: z.number().int().positive().max(3650).optional(),
});
/** Returned ONCE at creation — includes the raw `token`; never returned again. */
export const CreatePersonalAccessTokenResultSchema = PersonalAccessTokenSchema.extend({
    /** The raw secret, e.g. `mphx_…`. Store it now; the API keeps only its hash. */
    token: z.string(),
});
export const ExchangePersonalAccessTokenInputSchema = z.object({
    /** The raw PAT secret as returned at creation (`mphx_…`). */
    token: z.string().min(1),
});
/**
 * Result of redeeming a PAT. Two shapes, distinguished by `mode`:
 *
 *  - `keycloak-token` (default / legacy): the PAT belongs to a Keycloak user, so
 *    it was exchanged (RFC 8693) for a short-lived Keycloak user token returned
 *    in `accessToken`. Forward `accessToken` as the downstream Bearer.
 *  - `raw-pat`: the PAT was minted under a Formant/Coherence (robotics) login and
 *    has no Keycloak identity to impersonate. There is nothing to exchange; the
 *    caller forwards the raw PAT itself downstream (cortex-api validates PATs
 *    natively). `accessToken` is absent; `scopes` carries the PAT's scopes.
 */
export const PersonalAccessTokenExchangeResultSchema = z.object({
    /** Redemption mode. Absent is treated as `keycloak-token` for backward compat. */
    mode: z.enum(['keycloak-token', 'raw-pat']).optional(),
    /** Short-lived Keycloak-signed user access token. Present only for `keycloak-token`. */
    accessToken: z.string().optional(),
    /** Always `Bearer`. */
    tokenType: z.string(),
    /** Seconds until the returned credential should be re-validated. */
    expiresIn: z.number(),
    /** The org the resulting credential is scoped to (mirrors the PAT). */
    orgId: z.string(),
    /** The PAT's scopes. Returned for `raw-pat` so the caller can gate on them. */
    scopes: z.array(z.string()).optional(),
    /** The subject the credential acts as (for `raw-pat`, the PAT's stored userId). */
    userId: z.string().optional(),
});
// ─── Formant token exchange (telemetry → Metaphysics) ────────────────────────
export const ExchangeFormantTokenInputSchema = z.object({
    /** Formant user access token issued by admin-api (`formant:claims.type = user`). */
    token: z.string().min(1),
});
/** Result of swapping a Formant user token for a Metaphysics access token. */
export const FormantTokenExchangeResultSchema = PersonalAccessTokenExchangeResultSchema.extend({
    /** Always present on this path — the Formant/Metaphysics bearer to send. */
    accessToken: z.string(),
    /** Metaphysics user id for the exchanged token. */
    userId: z.string().uuid(),
});
/** Subset of admin-api `GET /users/:id` used during token exchange. */
export const FormantAdminUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email().optional().nullable(),
    enabled: z.boolean(),
    firstName: z.string().optional().nullable(),
    lastName: z.string().optional().nullable(),
    /** Share-scope tags (`GET /users/:id`). Reserved `$.…` keys are stripped by ACL. */
    tags: z.record(z.string(), z.string()).nullish().default({}),
});
/**
 * Subset of admin-api `POST /devices/query` used for share-scope ACL. The
 * caller's own scope is applied server-side, so `items` is exactly the set of
 * requested devices the caller may see — no tag matching needed on our side.
 */
export const FormantAdminDeviceListSchema = z.object({
    items: z.array(z.object({
        id: z.string(),
        /**
         * The device's Formant tags (`ITags`), used to evaluate place tag bindings
         * (DEV-17679). Lenient on purpose: a malformed tag must never fail the
         * visibility check that shares this call.
         */
        tags: z.record(z.string(), z.unknown()).nullish().catch(undefined),
    })),
});
/** Subset of admin-api `GET /organizations/:id` used during token exchange. */
export const FormantAdminOrganizationSchema = z.object({
    id: z.string().uuid(),
    enabled: z.boolean(),
    name: z.string().optional().nullable(),
    metaphysicsOrgId: z.string().uuid().nullable().optional(),
});
/**
 * Subset of admin-api `GET /groups` items the Daily Brief gather reads. A
 * group is "robots whose tag `tagKey` equals `tagValue`".
 */
export const FormantAdminGroupSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    tagKey: z.string(),
    tagValue: z.string(),
    active: z.boolean().optional().default(true),
    enabled: z.boolean().optional().default(true),
})
    .passthrough();
/**
 * Subset of admin-api `GET /users` items the Daily Brief gather reads: who can
 * see what. A tag set is `{ key: [values] }`; a robot is visible when every
 * key of SOME set lists the robot's value. A user with no tag set sees the
 * whole fleet.
 */
export const FormantAdminUserScopeSchema = z
    .object({
    id: z.string(),
    enabled: z.boolean().optional().default(true),
    scope: z
        .object({
        tags: z.array(z.record(z.string(), z.array(z.string()))).nullish(),
        devices: z
            .object({ tags: z.array(z.record(z.string(), z.array(z.string()))).nullish() })
            .passthrough()
            .nullish(),
    })
        .passthrough()
        .nullish(),
})
    .passthrough();
/** Subset of admin-api `GET /tag-templates` items: which tag keys define groups. */
export const FormantAdminTagTemplateSchema = z
    .object({ tagKey: z.string(), isGroup: z.boolean().optional().default(false) })
    .passthrough();
/** Subset of admin-api `POST /device-details/query` (and `/devices/query`) items. */
export const FormantAdminDeviceDetailSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    tags: z.record(z.string(), z.string()).nullish().default({}),
})
    .passthrough();
const FormantAdminViewModuleSchema = z
    .object({
    /** The title the module shows on the view, as saved by its editor. */
    name: z.string().nullish(),
    graph: z
        .object({
        id: z.string().optional(),
        nodes: z.array(z
            .object({
            id: z.string(),
            type: z.string(),
            /** `simple-module:<id the node had when the view was loaded>`. */
            group: z.string().nullish(),
            attributes: z
                .array(z.object({ name: z.string(), value: z.string() }).passthrough())
                .optional(),
        })
            .passthrough()),
    })
        .passthrough(),
})
    .passthrough();
/**
 * Coherence saves a view's modules as a record keyed by the module's layout
 * key, which is its graph id. The Formant API also accepts, and Coherence
 * renders, a view whose `modules` is a plain array (a view written by a
 * script or an older client), so that shape is read too, keyed the same way.
 */
function modulesAsRecord(value) {
    if (!Array.isArray(value))
        return value;
    return Object.fromEntries(value.map((module, index) => {
        const graphId = module?.graph?.id;
        return [typeof graphId === 'string' && graphId ? graphId : `module-${index}`, module];
    }));
}
/**
 * Subset of admin-api `GET /views/:id`: enough to confirm a module node sits
 * on the view and to read its config. `level` is absent until telemetry ships
 * the column.
 */
export const FormantAdminViewSchema = z
    .object({
    id: z.string(),
    name: z.string().nullish(),
    level: z.string().nullish(),
    layout: z
        .object({
        modules: z.preprocess(modulesAsRecord, z.record(z.string(), FormantAdminViewModuleSchema).optional().default({})),
    })
        .passthrough()
        .nullish(),
})
    .passthrough();
// ─── Magic-link login ────────────────────────────────────────────────────────
export const LoginRequestInputSchema = z.object({
    email: z.string().email(),
    /** Optional: which org slug. If the email maps to a user in exactly one org, we default to that. */
    orgSlug: z.string().optional(),
});
export const LoginRequestResultSchema = z.object({
    /** Always `"sent"` from the API (never reveals whether the email exists). */
    status: z.literal('sent'),
    /** Present only when AUTH_EMAIL_PROVIDER=console in dev — the raw magic-link URL so you can click it. */
    devLink: z.string().optional(),
});
export const VerifyMagicLinkInputSchema = z.object({
    token: z.string().min(10),
});
export const AuthTokensSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    user: UserSchema,
});
export const RefreshInputSchema = z.object({
    refreshToken: z.string(),
});
export const LogoutInputSchema = z.object({
    refreshToken: z.string(),
});
// ─── Invites ─────────────────────────────────────────────────────────────────
export const InviteSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    email: z.string().email(),
    role: UserRoleSchema,
    invitedByUserId: z.string(),
    expiresAt: z.date(),
    acceptedAt: z.date().nullable(),
    revokedAt: z.date().nullable(),
    createdAt: z.date(),
});
export const CreateInviteInputSchema = z.object({
    email: z.string().email(),
    role: UserRoleSchema.default('EDITOR'),
});
export const AcceptInviteInputSchema = z.object({
    token: z.string().min(10),
    name: z.string().min(1).max(200).optional(),
});
//# sourceMappingURL=auth.js.map